<template>
    <b-modal
        ref="workflowModal"
        :title="isEditMode ? '워크플로우 수정' : '워크플로우 생성'"
        size="md"
        @shown="handleShown"
        @hidden="handleHidden"
    >
        <form @submit.prevent="handleSave">
            <div class="form-group">
                <label>조직 <span class="text-danger">*</span></label>
                <b-form-select
                    v-model="form.orgIdx"
                    :options="orgOptions"
                    class="form-control"
                    required
                    :disabled="loadingOrgs"
                >
                    <template #first>
                        <b-form-select-option :value="null">조직을 선택하세요</b-form-select-option>
                    </template>
                </b-form-select>
                <small v-if="loadingOrgs" class="form-text text-muted">조직 목록을 불러오는 중...</small>
            </div>
            <div class="form-group">
                <label>워크플로우 이름 <span class="text-danger">*</span></label>
                <input
                    v-model="form.workflowName"
                    type="text"
                    class="form-control"
                    placeholder="예: 2026년 탄소배출 산정"
                    required
                />
            </div>
            <div class="form-group">
                <label>시작 월 <span class="text-danger">*</span></label>
                <DatePicker
                    v-model="fromMonthFormatted"
                    type="month"
                    format="YYYY-MM"
                    value-type="format"
                    placeholder="시작 월 선택"
                    :clearable="false"
                    @change="handleFromMonthChange"
                />
            </div>
            <div class="form-group">
                <label>종료 월 <span class="text-danger">*</span></label>
                <DatePicker
                    v-model="toMonthFormatted"
                    type="month"
                    format="YYYY-MM"
                    value-type="format"
                    placeholder="종료 월 선택"
                    :clearable="false"
                    @change="handleToMonthChange"
                />
            </div>
            <div class="form-group">
                <label>설명</label>
                <textarea
                    v-model="form.description"
                    class="form-control"
                    rows="3"
                    placeholder="워크플로우에 대한 설명을 입력하세요"
                ></textarea>
            </div>
        </form>
        <template #modal-footer>
            <b-button variant="secondary" @click="closeModal">취소</b-button>
            <b-button variant="primary" @click="handleSave">
                {{ "저장" }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import carbonApi from "../../../api";
import { unwrapResponse } from "../../../utils/api.js";

export default {
    name: "WorkflowFormModal",
    components: {
        DatePicker,
    },
    props: {
        isEditMode: {
            type: Boolean,
            default: false,
        },
        initialData: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            loadingOrgs: false,
            orgOptions: [],
            form: {
                orgIdx: null,
                workflowName: "",
                fromMonth: "",
                toMonth: "",
                description: "",
            },
            fromMonthFormatted: "",
            toMonthFormatted: "",
        };
    },
    watch: {
        initialData: {
            immediate: true,
            handler(newVal) {
                if (newVal && Object.keys(newVal).length > 0) {
                    this.form = {
                        orgIdx: newVal.orgIdx || null,
                        workflowName: newVal.workflowName || "",
                        fromMonth: newVal.fromMonth || "",
                        toMonth: newVal.toMonth || "",
                        description: newVal.description || "",
                    };
                    // YYYYMM 형식을 YYYY-MM 형식으로 변환
                    if (this.form.fromMonth && this.form.fromMonth.length === 6) {
                        this.fromMonthFormatted = `${this.form.fromMonth.substring(0, 4)}-${this.form.fromMonth.substring(4, 6)}`;
                    }
                    if (this.form.toMonth && this.form.toMonth.length === 6) {
                        this.toMonthFormatted = `${this.form.toMonth.substring(0, 4)}-${this.form.toMonth.substring(4, 6)}`;
                    }
                } else {
                    this.resetForm();
                }
            },
        },
    },
    methods: {
        async loadOrganizations() {
            try {
                this.loadingOrgs = true;
                const response = await carbonApi.organization.getOrgs();
                const data = unwrapResponse(response, { defaultMessage: "조직 목록을 불러오지 못했습니다." });

                // API 응답 구조: { success: true, data: { organizations: [...], count: 6 } }
                // unwrapResponse는 data를 반환하므로 { organizations: [...], count: 6 } 형태
                let orgs = [];
                if (Array.isArray(data)) {
                    orgs = data;
                } else if (data?.organizations && Array.isArray(data.organizations)) {
                    orgs = data.organizations;
                } else if (data?.items && Array.isArray(data.items)) {
                    orgs = data.items;
                } else if (data?.data && Array.isArray(data.data)) {
                    orgs = data.data;
                }

                this.orgOptions = orgs.map((org) => ({
                    value: org.orgIdx,
                    text: `${org.orgName}${org.orgCode ? ` (${org.orgCode})` : ""}`,
                }));
            } catch (error) {
                console.error("조직 목록 조회 실패:", error);
                this.$bvToast.toast("조직 목록을 불러오는데 실패했습니다.", {
                    title: "오류",
                    variant: "danger",
                    autoHideDelay: 3000,
                });
            } finally {
                this.loadingOrgs = false;
            }
        },
        show() {
            this.$refs.workflowModal.show();
        },
        hide() {
            this.$refs.workflowModal.hide();
        },
        closeModal() {
            this.hide();
        },
        handleShown() {
            // 모달이 열릴 때 조직 목록이 없으면 조회
            if (this.orgOptions.length === 0 && !this.loadingOrgs) {
                this.loadOrganizations();
            }
        },
        handleHidden() {
            this.resetForm();
            this.$emit("hidden");
        },
        resetForm() {
            this.form = {
                orgIdx: null,
                workflowName: "",
                fromMonth: "",
                toMonth: "",
                description: "",
            };
            this.fromMonthFormatted = "";
            this.toMonthFormatted = "";
        },
        handleFromMonthChange(value) {
            // YYYY-MM 형식을 YYYYMM 형식으로 변환
            if (value) {
                this.form.fromMonth = value.replace(/-/g, "");
            } else {
                this.form.fromMonth = "";
            }
        },
        handleToMonthChange(value) {
            // YYYY-MM 형식을 YYYYMM 형식으로 변환
            if (value) {
                this.form.toMonth = value.replace(/-/g, "");
            } else {
                this.form.toMonth = "";
            }
        },
        async handleSave() {
            // 유효성 검사
            if (!this.form.orgIdx || !this.form.workflowName || !this.form.fromMonth || !this.form.toMonth) {
                this.$bvToast.toast("필수 항목을 모두 입력해주세요.", {
                    title: "입력 오류",
                    variant: "warning",
                    autoHideDelay: 3000,
                });
                return;
            }

            // 날짜 형식 검증
            const monthPattern = /^[0-9]{6}$/;
            if (!monthPattern.test(this.form.fromMonth) || !monthPattern.test(this.form.toMonth)) {
                this.$bvToast.toast("시작 월과 종료 월을 올바르게 선택해주세요.", {
                    title: "입력 오류",
                    variant: "warning",
                    autoHideDelay: 3000,
                });
                return;
            }

            // 시작 월이 종료 월보다 큰지 확인
            if (parseInt(this.form.fromMonth) > parseInt(this.form.toMonth)) {
                this.$bvToast.toast("시작 월은 종료 월보다 작거나 같아야 합니다.", {
                    title: "입력 오류",
                    variant: "warning",
                    autoHideDelay: 3000,
                });
                return;
            }

            const payload = {
                orgIdx: this.form.orgIdx,
                workflowName: this.form.workflowName.trim(),
                fromMonth: this.form.fromMonth.trim(),
                toMonth: this.form.toMonth.trim(),
            };

            // description이 비어있지 않으면 추가
            if (this.form.description && this.form.description.trim()) {
                payload.description = this.form.description.trim();
            }

            this.$emit("save", payload);
        },
    },
};
</script>

<style scoped>
.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: 14px;
    color: #1e293b;
}

.form-control {
    width: 100%;
    /* padding: 8px 12px; */
    font-size: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    transition: border-color 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.text-danger {
    color: #ef4444;
}

.form-text {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
}

textarea.form-control {
    resize: vertical;
    min-height: 80px;
}

/* DatePicker 스타일 조정 */
::v-deep .mx-datepicker {
    width: 100%;
}

::v-deep .mx-input-wrapper {
    width: 100%;
}

::v-deep .mx-input {
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

::v-deep .mx-input:focus {
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}
</style>
